import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead from '../components/SEOHead';
import './Blogs.css';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.startsWith('/') ? '' : '/media/'}${path}`;
};

const formatBlogDate = (dateStr, language) => {
  try {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const isKa = String(language || '').toLowerCase().startsWith('ka');
    if (isKa) {
      const kaMonths = [
        'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
        'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი',
      ];
      return `${kaMonths[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return dateStr;
  }
};

const Blogs = () => {
  const { i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/content/blogs/');
        setItems(res.data?.results || res.data || []);
      } catch (e) {
        setError(i18n.language === 'ka' ? 'ბლოგების ჩატვირთვა ვერ მოხერხდა' : 'Failed to load blogs');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  const localizedItems = useMemo(() => {
    const isKa = String(i18n.language || '').toLowerCase().startsWith('ka');
    return items.map((it) => {
      const title = (isKa ? it.title_ka || it.title_en : it.title_en || it.title_ka) || '';
      const short =
        (isKa ? it.short_description_ka || it.short_description_en : it.short_description_en || it.short_description_ka) || '';
      return {
        ...it,
        title,
        short,
        dateLabel: formatBlogDate(it.date, i18n.language),
        photoUrl: getImageUrl(it.card_photo),
      };
    });
  }, [items, i18n.language]);

  const titleLabel = i18n.language === 'ka' ? 'ბლოგები' : 'Blogs';

  return (
    <div className="blogs-page">
      <SEOHead
        title="Blog | GFM Studio"
        description="Read the latest news, insights, and stories from GFM Studio."
        path="/history/blogs"
      />
      <div className="blogs-header">
        <h1>{titleLabel}</h1>
        <div className="blogs-header-line" />
      </div>

      {loading ? (
        <div className="blogs-state">{i18n.language === 'ka' ? 'იტვირთება...' : 'Loading...'}</div>
      ) : error ? (
        <div className="blogs-state">{error}</div>
      ) : localizedItems.length === 0 ? (
        <div className="blogs-state">{i18n.language === 'ka' ? 'ბლოგები ვერ მოიძებნა' : 'No blogs found'}</div>
      ) : (
        <div className="blogs-grid">
          {localizedItems.map((it) => (
            <Link key={it.id} to={`/history/blogs/${it.slug}`} className="blog-card blog-card-link">
              <div className="blog-card-media">
                {it.photoUrl ? <img src={it.photoUrl} alt={it.title} loading="lazy" /> : <div className="blog-card-media-placeholder" />}
                <div className="blog-card-media-overlay" />
                {it.dateLabel ? <div className="blog-card-date">{it.dateLabel}</div> : null}
              </div>

              <div className="blog-card-body">
                <h2 className="blog-card-title">{it.title}</h2>
                {it.short ? <p className="blog-card-excerpt">{it.short}</p> : null}
                <div className="blog-card-read">
                  <span>{i18n.language === 'ka' ? 'წაიკითხე' : 'Read'}</span>
                  <span className="blog-card-read-arrow">→</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Blogs;

